import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { GridBooleanColumnConfig } from '../../components/paged-grid/models/grid-boolean-column.config';
import { GridColumnConfig } from '../../components/paged-grid/models/grid-column.config';
import { GridDateColumnConfig } from '../../components/paged-grid/models/grid-date-column.config';
import { GridNumberColumnConfig } from '../../components/paged-grid/models/grid-number-column.config';
import { GridConfig } from '../../components/paged-grid/models/grid.config';
import { PagedGridComponent } from '../../components/paged-grid/paged-grid.component';
import { ModelTypes } from '../../core/utils/mappers/model.types';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { RelatedItemModel } from '../../models/core/related-item.model';
import { ListTransactionModel } from '../../models/list-transaction.model';
import { TransactionsResource } from '../../services/resources/transactions.resource';

@Component({
    selector: 'sl-transactions',
    templateUrl: './transactions.component.html',
    styleUrls: ['./transactions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransactionsComponent implements OnInit {
    gridConfig: GridConfig<ListTransactionModel>;

    private readonly transactionsResource = inject(TransactionsResource);

    ngOnInit(): void {
        const columns = [
            new GridNumberColumnConfig({ fieldName: 'id', header: 'General.Id' }),
            new GridColumnConfig({ fieldName: 'transactionTypeName', header: 'Transactions.Type' }),
            new GridColumnConfig({ fieldName: 'outgoingName', header: 'Transactions.OutgoingName' }),
            new GridColumnConfig({ fieldName: 'incomingName', header: 'Transactions.IncomingName' }),
            new GridNumberColumnConfig({
                fieldName: 'registeredNumberOfShares',
                header: 'Transactions.RegisteredNumberOfShares',
                isTrendingValue: true,
                displayValue: false,
                trendingThreshold: 0
            }),
            new GridNumberColumnConfig({ fieldName: 'numberOfShares', header: 'Transactions.Shares' }),
            new GridNumberColumnConfig({
                fieldName: 'registeredShareBalance',
                header: 'Transactions.RegisteredShareBalance'
            }),
            new GridBooleanColumnConfig({
                fieldName: 'isPending',
                header: 'Transactions.Pending',
                isIcon: true,
                trueText: 'hourglass_empty',
                falseText: ''
            }),
            new GridDateColumnConfig({ fieldName: 'valueDate', header: 'General.Date' }),
            new GridBooleanColumnConfig({
                fieldName: 'hasAuthorityToTransfer',
                header: 'Transactions.HasAuthorityToTransfer',
                isHidden: true
            }),
            new GridDateColumnConfig({ fieldName: 'lockedUntilDate', header: 'Transactions.LockedUntilDate', isHidden: true }),
            new GridDateColumnConfig({ fieldName: 'createdDate', header: 'Audit.CreatedDate', isHidden: true }),
            new GridColumnConfig({ fieldName: 'createdByUserUsername', header: 'Audit.CreatedBy', isHidden: true }),
            new GridColumnConfig({ fieldName: 'executedByUserUsername', header: 'Audit.ExecutedBy', isHidden: true }),
            new GridColumnConfig({ fieldName: 'approvedByUserUsername', header: 'Audit.ApprovedBy', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'remarks',
                header: 'General.Remarks',
                isHidden: true,
                requiredPermission: 'GridColumnTransactionRemarks'
            }),
            new GridColumnConfig({ fieldName: 'securityISINNr', header: 'Transactions.IsinNr', isHidden: true })
        ];
        this.gridConfig = new GridConfig<ListTransactionModel>({
            name: 'transactions',
            typeName: ModelTypes.transactions,
            resource: this.transactionsResource,
            pageSize: 10,
            hasViewButton: true,
            columns,
            relatedActionsHandler: (relatedItem: RelatedItemModel, grid: PagedGridComponent<ListTransactionModel>) => {
                if (relatedItem.commandName === 'ApproveTransactionCommand' && !!relatedItem.referencedButton) {
                    const btn = relatedItem.referencedButton;
                    btn.isDisabled = grid.selectedRows.length < 1;
                    btn.clickFunction = () => {
                        btn.isLoading = true;
                        const requests: Observable<ApiResponseModel<boolean>>[] = [];

                        grid.selectedRows.forEach(id => requests.push(this.transactionsResource.approve(id)));

                        forkJoin(requests)
                            .pipe(
                                finalize(() => {
                                    grid.processData();
                                    btn.isLoading = false;
                                })
                            )
                            .subscribe();
                    };
                }
            }
        });
    }
}
